import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Container, Paper, Avatar } from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { processImage } from "../services/imageService";

const StyledButton = styled(Button)(({ theme }) => ({
    fontWeight: "bold",
    padding: "12px 28px",
    fontSize: "16px",
    borderRadius: "8px",
    backgroundColor: "#800020",
    color: "#FFD700",
    '&:hover': {
        backgroundColor: "#660018",
        transform: "scale(1.05)",
    },
}));

const UploadPage = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setError("");
        if (file) {
            setPreview(URL.createObjectURL(file));
        } else {
            setPreview(null);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError("Please select an image to upload.");
            return;
        }
        try {
            const embedding = await processImage(selectedFile);
            const uploadedImage = URL.createObjectURL(selectedFile);

            navigate("/results", {
                state: {
                    embedding,
                    uploadedImage,
                },
            });
        } catch (error) {
            setError("Error processing image. Please try again.");
            console.error("Error processing image:", error);
        }
    };

    return (
        <Box sx={{ backgroundColor: "#fff", minHeight: "100vh", py: 10 }}>
            <Container maxWidth="sm" component={motion.div} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
                <Typography
                    variant="h2"
                    align="center"
                    sx={{ fontFamily: "Georgia, serif", color: "#008080", fontWeight: "bold", mb: 2 }}
                >
                    Art For My Room
                </Typography>
                <Typography
                    variant="h6"
                    align="center"
                    sx={{ color: "#800020", fontWeight: 500, mb: 4 }}
                >
                    Discover the perfect painting for your wall
                </Typography>

                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        borderRadius: 4,
                        textAlign: "center",
                        mb: 4,
                        maxWidth: 500,
                        mx: "auto",
                    }}
                >
                    <Avatar
                        sx={{
                            bgcolor: "#e6f5f5",
                            width: 64,
                            height: 64,
                            mx: "auto",
                            mb: 2,
                        }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 40, color: "#008080" }} />
                    </Avatar>
                    <Typography variant="h5" sx={{ color: "#0a174e", fontWeight: 600, mb: 2 }}>
                        Upload an Image
                    </Typography>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        id="image-upload"
                        style={{ display: "none" }}
                    />
                    <label htmlFor="image-upload">
                        <StyledButton component="span" fullWidth>
                            Select Image
                        </StyledButton>
                    </label>

                    {preview && (
                        <Box sx={{ mt: 3 }}>
                            <img src={preview} alt="Preview" style={{ maxWidth: "100%", borderRadius: 8 }} />
                        </Box>
                    )}

                    {error && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <StyledButton
                        variant="contained"
                        onClick={handleUpload}
                        fullWidth
                        sx={{ mt: 3 }}
                        disabled={!selectedFile}
                    >
                        Find Your Perfect Painting
                    </StyledButton>
                </Paper>

                <Box sx={{ textAlign: "center", mt: 6, px: 2 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontStyle: "italic", color: "#008080", maxWidth: 700, mx: "auto" }}
                    >
                        "Enhancing your room with art"
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 2 }}>
                        Made for UK homes | artformyroom.com 2025
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default UploadPage;
